import { Link } from "@inertiajs/react"
import { Flame, Sparkles } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ProgressRing } from "./progress-ring"
import { MockTextBadge, MockBadge } from "./mock-data-wrapper"
import type { DashboardFlashcardsStats, DashboardMockStats } from "@/types"

interface DashboardHeroProps {
  flashcards: DashboardFlashcardsStats
  mockStats: DashboardMockStats
}

export function DashboardHero({ flashcards, mockStats }: DashboardHeroProps) {
  const masteryPercentage =
    flashcards.totalCount > 0
      ? Math.round((flashcards.masteredCount / flashcards.totalCount) * 100)
      : 0

  return (
    <div className="border-border relative overflow-hidden rounded-2xl border bg-gradient-to-br from-slate-50 via-white to-indigo-50/50 dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950/30">
      {/* Decorative background elements */}
      <div className="bg-primary/5 pointer-events-none absolute -top-20 -right-20 size-64 rounded-full blur-3xl" />
      <div className="bg-accent/5 pointer-events-none absolute -bottom-20 -left-20 size-64 rounded-full blur-3xl" />

      <div className="relative px-6 py-8 sm:px-8 sm:py-10">
        <div className="flex flex-col items-center gap-8 sm:flex-row sm:items-start sm:justify-between">
          {/* Left: Title and stats */}
          <div className="flex-1 text-center sm:text-left">
            <h1 className="font-display text-foreground text-3xl font-bold tracking-tight sm:text-4xl">
              Your Thai Journey
            </h1>
            <div className="mt-3">
              <MockTextBadge label="preview">
                Level {mockStats.level} · {mockStats.wordsLearned} words learned
              </MockTextBadge>
            </div>

            {/* Streak badge - mock data */}
            {mockStats.streak > 0 && (
              <div className="mt-4">
                <MockBadge>
                  <Badge
                    variant="secondary"
                    className="gap-1.5 bg-amber-100 px-3 py-1.5 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300"
                  >
                    <Flame className="size-4" />
                    {mockStats.streak} day streak
                  </Badge>
                </MockBadge>
              </div>
            )}

            {/* CTA Button */}
            <div className="mt-6">
              {flashcards.dueCount > 0 ? (
                <Link href="/flashcards/due">
                  <Button
                    size="lg"
                    className="shadow-primary/20 gap-2 shadow-lg"
                  >
                    <Sparkles className="size-5" />
                    Practice {flashcards.dueCount} Due Card
                    {flashcards.dueCount !== 1 ? "s" : ""}
                  </Button>
                </Link>
              ) : (
                <Link href="/conversations">
                  <Button size="lg" variant="outline" className="gap-2">
                    <Sparkles className="size-5" />
                    Start a Conversation
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Right: Progress Ring */}
          <div className="flex-shrink-0">
            <ProgressRing
              value={masteryPercentage}
              size={140}
              strokeWidth={10}
              className="drop-shadow-lg"
            >
              <span className="text-foreground text-3xl font-bold">
                {masteryPercentage}%
              </span>
              <span className="text-muted-foreground text-xs">mastered</span>
            </ProgressRing>
          </div>
        </div>
      </div>
    </div>
  )
}
